/* eslint-disable class-methods-use-this */
const { cloneDeep } = require('lodash');
const { deliveryTypes: DeliveryType, PaymentCharge, sourceAndDestinationType, transitDelaySlabTypes, CHARGE_MASTER_TYPES } = require('pando-constants');
const { groupBy, isEmpty } = require('lodash');

const Freight = require('./implementations/payments/freight');
const BaseDieselRate = require('./implementations/payments/bdr');
const Loading = require('./implementations/payments/loading');
const Unloading = require('./implementations/payments/unloading');
const LoadingDetention = require('./implementations/payments/loadingDetention');
const UnloadingDetention = require('./implementations/payments/unloadingDetention');
const Shortage = require('./implementations/payments/shortage');
const Damage = require('./implementations/payments/damage');
const CartonDamage = require('./implementations/payments/cartonDamage');
const ArrivalDelay = require('./implementations/payments/arrivalDelay');
const TransitDelay = require('./implementations/payments/transitDelay');
const AdditionalPoint = require('./implementations/payments/additionalPoint');
const Oda = require('./implementations/payments/oda');
const FixedCharge = require('./implementations/payments/fixedCharge');
const CustomCharge = require('./implementations/payments/customCharge');
const TotalAmount = require('./implementations/payments/total');
const Appointment = require('./implementations/payments/appointment');
const MallCharge = require('./implementations/payments/mallCharge');
const ECC = require('./implementations/payments/eccCharge');
const { indentStatus } = require('./constants/payments');
const CustomSourceCharge = require('./implementations/payments/customSourceCharge');
const CustomDestinationCharge = require('./implementations/payments/customDestinationCharge');

class Payments {
  constructor(strategies, data = {}, isMaxFreightBdrEnabled = false, isTransporterDebitEnabled = false, includeCustomChargeForOpenIndent = false, walletMaterialBifurcation = false) {
    this.strategies = strategies;
    this.data = data;
    this.isMaxFreightBdrEnabled = isMaxFreightBdrEnabled;
    this.isTransporterDebitEnabled = isTransporterDebitEnabled;
    this.includeCustomChargeForOpenIndent = includeCustomChargeForOpenIndent;
    this.walletMaterialBifurcation = walletMaterialBifurcation;
  }

  computePaymentCharges(masterData, depotCalenderMap = {}, clientConfiguration = {},
    depotConfiguration = {}, configOptions = {}, idMap = {}) {
    const validCharges = ['freight', 'total'];
    const invalidChargesInDispatch = ['transitDelay', 'unloadingDetention', 'rtoCharges'];
    const paymentCharges = {
      hillyRegionCharge: 0,
      rov_charge: 0,
      discrepancies: {
        loadingDelay: 0,
        unloadingDelay: 0,
        shortage: 0,
        damage: 0,
        cartonDamage: 0,
        transitDelay: 0,
        transporterDebit: 0,
      },
      sdlcMap: {
        shortage: {},
        damage: {},
        cartonDamage: {},
      } };
    const enablePayInDIspatch = !!(clientConfiguration
      && clientConfiguration.payment_in_dispatch_status
        && clientConfiguration.payment_in_dispatch_status.enabled === true);
    // compute charge based on payment strategies
    const useDestinationCharges = clientConfiguration.default_charge_master === CHARGE_MASTER_TYPES.DESTINATION_CHARGE_MASTER;
    for (let strategyIndex = 0; strategyIndex < this.strategies.length; strategyIndex += 1) {
      const strategy = this.strategies[strategyIndex];
      /**
       * strategy would be key value pair.
       *  key is payment charge type - freight / shortage /loading and so on.
       *  value is strategy type - shortage by mrp / shortage by invoice / odaCharges and so on.
       */
      const paymentChargeType = Object.keys(strategy)[0];
      let computeCharge = (masterData.status === indentStatus.CANCELLED) ?
        validCharges.includes(paymentChargeType) : true;
      if (enablePayInDIspatch && masterData.status === indentStatus.DISPATCHED) {
        computeCharge = !invalidChargesInDispatch.includes(paymentChargeType);
      }

      if (computeCharge) {
        switch (paymentChargeType) {
          case PaymentCharge.FREIGHT: {
            const freight = new Freight();
            const freightDetails = freight[strategy[paymentChargeType]](masterData,
              clientConfiguration, configOptions);
            paymentCharges[paymentChargeType] = freightDetails && freightDetails.freightCharge;
            // used to display freight calculation details in invoice slider page.
            paymentCharges.freightCalculation = freightDetails;
            paymentCharges.hillyRegionCharge = freightDetails.hillyRegionCharge || 0;
            paymentCharges.rovCharge = freightDetails.rovCharge || 0;
            if (freightDetails.rtoCharges) {
              paymentCharges.rtoCharges = freightDetails.rtoCharges;
            }
            if (freightDetails.returnFreightCharge) {
              paymentCharges.returnFreightCharge = freightDetails.returnFreightCharge;
            }
            break;
          }
          case PaymentCharge.BDR: {
            const bdrCharge = new BaseDieselRate(this.isMaxFreightBdrEnabled);
            paymentCharges[paymentChargeType] = bdrCharge[strategy[paymentChargeType]](masterData, clientConfiguration);
            break;
          }
          case PaymentCharge.LOADING: {
            const loadingCharge = new Loading();
            // excute current payment charge strategy.
            paymentCharges[paymentChargeType] =
           loadingCharge[strategy[paymentChargeType]](masterData, depotConfiguration, idMap);
            break;
          }
          case PaymentCharge.UNLOADING: {
            const unLoadingCharge = new Unloading();
            paymentCharges[paymentChargeType] =
           unLoadingCharge[strategy[paymentChargeType]](masterData, depotConfiguration, useDestinationCharges);
            break;
          }
          case PaymentCharge.LOADINGDETENTION: {
            const loadingDetention = new LoadingDetention();
            paymentCharges[paymentChargeType] =
           loadingDetention[strategy[paymentChargeType]](masterData, depotCalenderMap, depotConfiguration);

            paymentCharges.discrepancies.loadingDelay = loadingDetention[strategy
              .loadingDetentionDays](masterData, depotCalenderMap, depotConfiguration);
            break;
          }
          case PaymentCharge.UNLOADINGDETENTION: {
            const unloadingDetention = new UnloadingDetention();
            paymentCharges[paymentChargeType] =
           unloadingDetention[strategy[paymentChargeType]](masterData, depotCalenderMap,
             depotConfiguration);

            paymentCharges.discrepancies.unloadingDelay =
           unloadingDetention[strategy.unloadingDetentionDays](masterData, depotCalenderMap,
             depotConfiguration);
            break;
          }
          case PaymentCharge.SHORTAGE: {
            if (this.isTransporterDebitEnabled) break;
            const shortageCharge = new Shortage();
            const { shortage, materialShortageDamageMap } =
              shortageCharge[strategy[paymentChargeType]](masterData, clientConfiguration);
            paymentCharges[paymentChargeType] = shortage;
            if (!paymentCharges.materialLevelSdcd) {
              paymentCharges.materialLevelSdcd = {};
            }
            if (!paymentCharges.materialLevelSdcd.shortage) {
              paymentCharges.materialLevelSdcd.shortage = {};
            }
            // eslint-disable-next-line guard-for-in, no-restricted-syntax
            for (const key in materialShortageDamageMap) {
              paymentCharges.materialLevelSdcd.shortage[key] = {
                ...materialShortageDamageMap[key],
              };
            }

            paymentCharges.discrepancies.shortage = shortageCharge.getTotalShortage(masterData);
            paymentCharges.sdlcMap.shortage = shortageCharge.getMaterialLevelShortage(masterData,
              clientConfiguration, this.walletMaterialBifurcation ? materialShortageDamageMap : {});
            break;
          }
          case PaymentCharge.DAMAGE: {
            if (this.isTransporterDebitEnabled) break;
            const damageCharge = new Damage();
            const { damage, materialShortageDamageMap } =
              damageCharge[strategy[paymentChargeType]](masterData, clientConfiguration);
            paymentCharges[paymentChargeType] = damage;
            if (!paymentCharges.materialLevelSdcd) {
              paymentCharges.materialLevelSdcd = {};
            }
            if (!paymentCharges.materialLevelSdcd.damage) {
              paymentCharges.materialLevelSdcd.damage = {};
            }
            // eslint-disable-next-line guard-for-in, no-restricted-syntax
            for (const key in materialShortageDamageMap) {
              paymentCharges.materialLevelSdcd.damage[key] = {
                ...materialShortageDamageMap[key],
              };
            }

            paymentCharges.discrepancies.damage = damageCharge.getTotalDamage(masterData);
            paymentCharges.sdlcMap.damage = damageCharge.getMaterialLevelDamage(masterData,
              clientConfiguration, this.walletMaterialBifurcation ? materialShortageDamageMap : {});
            break;
          }
          case PaymentCharge.CARTONDAMAGE: {
            if (this.isTransporterDebitEnabled) break;
            const cartonDamageCharge = new CartonDamage();
            const { cartonDamage, materialShortageDamageMap } =
            cartonDamageCharge[strategy[paymentChargeType]](masterData, clientConfiguration);
            paymentCharges[paymentChargeType] = cartonDamage;
            if (!paymentCharges.materialLevelSdcd) {
              paymentCharges.materialLevelSdcd = {};
            }
            if (!paymentCharges.materialLevelSdcd.cartonDamage) {
              paymentCharges.materialLevelSdcd.cartonDamage = {};
            }
            // eslint-disable-next-line guard-for-in, no-restricted-syntax
            for (const key in materialShortageDamageMap) {
              paymentCharges.materialLevelSdcd.cartonDamage[key] = {
                ...materialShortageDamageMap[key],
              };
            }

            paymentCharges.discrepancies.cartonDamage = cartonDamageCharge
              .cartonDamageCount(masterData);
            paymentCharges.sdlcMap.cartonDamage = cartonDamageCharge
              .getMaterialLevelCartonDamage(masterData, clientConfiguration, this.walletMaterialBifurcation ? materialShortageDamageMap : {});
            break;
          }
          case PaymentCharge.ARRIVALDELAY: {
            const arrivalDelay = new ArrivalDelay();
            paymentCharges[paymentChargeType] = arrivalDelay[strategy[paymentChargeType]](masterData);
            break;
          }
          case PaymentCharge.TRANSITDELAY: {
            const transitDelay = new TransitDelay();
            let freightCharge = 0;
            const freight = new Freight();
            const freightStrategy = this.strategies.find(strategyDetail => Object.keys(strategyDetail)[0] === 'freight');
            const freightStrategyType = Object.values(freightStrategy)[0];
            const freightDetails = freight[freightStrategyType](masterData, clientConfiguration);
            freightCharge = (freightDetails && freightDetails.freightCharge);

            paymentCharges[paymentChargeType] = transitDelay[strategy[paymentChargeType]](
              masterData, freightCharge, depotCalenderMap);
            paymentCharges.discrepancies.transitDelay = transitDelay
              .calculateTransitDelaysDays(masterData, depotCalenderMap);
            break;
          }
          case PaymentCharge.ADDITIONALPOINT: {
            const additionalPointCharge = new AdditionalPoint();
            paymentCharges[paymentChargeType] =
            additionalPointCharge[strategy[paymentChargeType]](masterData);
            break;
          }
          case PaymentCharge.CUSTOMSOURCECHARGE: {
            const customSourceCharge = new CustomSourceCharge();
            const sourceChargesOutcome = customSourceCharge.perform(masterData, strategy[paymentChargeType]);
            if (!isEmpty(sourceChargesOutcome)) {
              paymentCharges[paymentChargeType] = sourceChargesOutcome;
            }
            break;
          }
          case PaymentCharge.CUSTOMDESTINATIONCHARGE: {
            const customDestinationCharge = new CustomDestinationCharge();
            const destinationChargesOutcome = customDestinationCharge.perform(masterData, strategy[paymentChargeType]);
            if (!isEmpty(destinationChargesOutcome)) {
              paymentCharges[paymentChargeType] = destinationChargesOutcome;
            }
            break;
          }
          case PaymentCharge.ODA: {
            const odaCharge = new Oda();
            paymentCharges[paymentChargeType] = odaCharge[strategy[paymentChargeType]](masterData, clientConfiguration);
            break;
          }
          case PaymentCharge.FIXEDCHARGE: {
            const fixedCharge = new FixedCharge();
            paymentCharges[paymentChargeType] = fixedCharge[strategy[paymentChargeType]](masterData);
            break;
          }
          case PaymentCharge.MALLCHARGE: {
            const mallCharge = new MallCharge();
            paymentCharges[paymentChargeType] = mallCharge[strategy[paymentChargeType]](masterData);
            break;
          }
          case PaymentCharge.APPOINTMENTCHARGE: {
            const appointmentCharge = new Appointment();
            paymentCharges[paymentChargeType] = appointmentCharge[strategy[paymentChargeType]](masterData, clientConfiguration);
            break;
          }
          case PaymentCharge.ECC: {
            const eccCharge = new ECC();
            paymentCharges[paymentChargeType] = eccCharge[strategy[paymentChargeType]](masterData);
            break;
          }
          case PaymentCharge.CUSTOMCHARGE: {
            const customCharge = new CustomCharge(this.includeCustomChargeForOpenIndent);
            paymentCharges[paymentChargeType] = customCharge[strategy[paymentChargeType]](masterData);
            break;
          }
          case PaymentCharge.TRANSPORTERDEBIT: {
            if (this.isTransporterDebitEnabled && !paymentCharges.transporterDebit) {
              const transporterDebit = masterData.destinations.reduce((total, destination) =>
                total + destination.materials.reduce((sum, material) =>
                  sum + (material.epod && material.epod.transporter_debit
                    ? material.epod.transporter_debit
                    : 0), 0), 0);
              paymentCharges.transporterDebit = transporterDebit;
              paymentCharges.discrepancies.transporterDebit = transporterDebit;
            }
            break;
          }
          default:
            this.totalAmount = 0;
        }
      }
    }
    return paymentCharges;
  }

  static bifurcateChargesByMaterialFraction(paymentCharges, materialFraction) {
    /**
     * defect charges will be calculated at material level based on tax, no tax materials.
     *  hence avoiding multiplication of material fraction for them
     * */
    const materialFractionExemptedCharges = [PaymentCharge.SHORTAGE,
      PaymentCharge.DAMAGE, PaymentCharge.CARTONDAMAGE];
    const charges = Object.values(PaymentCharge);
    /**
     * payment charges will contain descripancy count as well.
     * assigning empty object to bifurcated charge will lead to the loss of descripancy count.
     */
    const bifurcatedCharge = paymentCharges;
    for (let chargeIndex = 0; chargeIndex < charges.length; chargeIndex += 1) {
      const chargeType = charges[chargeIndex];
      // determine material fraction based on payment charge
      const fraction = materialFractionExemptedCharges.includes(chargeType) ?
        1 : materialFraction;
      bifurcatedCharge[chargeType] =
      Number(paymentCharges[chargeType] * fraction).toFixed(2);
    }
    return bifurcatedCharge;
  }

  groupConsignees(masterData) {
    const destinations = masterData.destinations;
    const consignees = groupBy(destinations, 'type')[sourceAndDestinationType.CONSIGNEE];
    const consigneesByGroup = groupBy(consignees, 'group');
    const groupNames = Object.keys(consigneesByGroup);
    groupNames.forEach((group) => {
      consigneesByGroup[group].forEach((consignee, index) => {
        // eslint-disable-next-line no-param-reassign
        consignee.include = (index === 0) ? 1 : 0;
      });
    });
    return masterData;
  }


  getFTLPaymentsCharges(data, depotCalenderMap = {}, clientConfiguration = {},
    depotConfiguration = {}, configOptions = {}) {
    let masterData = cloneDeep(data);
    masterData = this.groupConsignees(masterData);
    const paymentCharge = this.computePaymentCharges(masterData, depotCalenderMap,
      clientConfiguration, depotConfiguration, configOptions);
    // calculated charges will be mapped under the corresponding consignee in the UI & table.
    // so assigning destination id in the result object.
    paymentCharge.destinationId = masterData.destinations[0].locationId.toString();
    data.billToPartyId = masterData.billToPartyId || null;
    data.billToPartyAmount = masterData.billToPartyAmount || {};
    data.primaryContractIds = masterData.primaryContractIds || [];

    const totalAmount = new TotalAmount();
    const totalPaymentCharge = totalAmount.totalAmountCalculation(paymentCharge);
    return [totalPaymentCharge];
  }

  getPTLPaymentsCharges(data, depotCalenderMap = {}, clientConfiguration = {},
    depotConfiguration = {}, configOptions = {}) {
    /**
     * clone deep master data
     *  since modifying destination details for ptl may affect sub sequent calls.
     */
    let masterData = cloneDeep(data);
    masterData = this.groupConsignees(masterData);
    const destinations = masterData.destinations;
    delete masterData.destinations;

    const ptlPaymentsCharges = [];
    const totalAmount = new TotalAmount();

    for (let destinationIndex = 0; destinationIndex < destinations.length; destinationIndex += 1) {
      // charges will be calculated for every consignee in ptl indent.
      const destination = destinations[destinationIndex];
      masterData.destinations = [destination];
      const idMap = {
        gateId: destination && destination.sourceGateId != null
          ? destination.sourceGateId.toString()
          : null,
        locationId: destination && destination.locationId != null
          ? destination.locationId.toString()
          : null,
      };
      const paymentCharges = this.computePaymentCharges(masterData, depotCalenderMap,
        clientConfiguration, depotConfiguration, configOptions, idMap);
      data.primaryContractIds = [...(data.primaryContractIds || []), ...(masterData.primaryContractIds || [])];

      // calculated charges will be mapped under the corresponding consignee in the UI & table.
      // so assigning destination id in the result object.
      paymentCharges.destinationId = destinations[destinationIndex].locationId.toString();
      const totalPaymentCharge = totalAmount.totalAmountCalculation(paymentCharges);
      ptlPaymentsCharges.push(totalPaymentCharge);
    }
    return ptlPaymentsCharges;
  }

  /**
   * calculate total amount for each payment charge in the array.
   * Used in UI for total amount computation.
   * @param {array of payment charges} charges
   */
  getPaymentChargesTotal(charges) {
    const totalAmount = new TotalAmount();
    const paymentsCharges = [];
    charges.forEach((charge) => {
      const payment = totalAmount.totalAmountCalculation(charge);
      paymentsCharges.push(payment);
    });
    return paymentsCharges;
  }
}

module.exports = Payments;

